/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  Feature,
  FeatureCollection,
  GeometryCollection,
  GeometryObject,
} from 'geojson';
import i18next from 'i18next';
import { groupBy } from 'lodash';
import { dateUtils } from '@opengeoweb/shared';
import type {
  Plot,
  PlotParameter,
  PlotPreset,
  TimeSeriesService,
} from '@opengeoweb/shared';
import type { syncGroupsTypes } from '@opengeoweb/store';
import type {
  EDRPositionResponse,
  EDRPositionResponseCoverageCollection,
  FailedParameterInfo,
  GeoJsonWithService,
  Parameter,
  ParameterWithData,
  PlotWithData,
  PointerLocation,
  TimeSeriesLocation,
  UseGetLocationReturnTypeProperties,
} from './types';
import {
  findNearestPoint,
  getFeatureCenterPoint,
  getUnit,
  serializeDimensions,
} from '../../utils/edrUtils';

/** Maps plot ID to array of error message strings */
export type PlotErrorsMap = Record<Plot['plotId'], string[]>;

/** Maps plot ID to array of failed parameter information */
export type FailedParametersMap = Record<Plot['plotId'], FailedParameterInfo[]>;

export const createGeoJsonArrays = (
  locations: (
    | FeatureCollection<GeometryObject, UseGetLocationReturnTypeProperties>
    | undefined
  )[],
  services: TimeSeriesService[] | undefined,
  hideFeatures: boolean,
  memoizedSelectedFeature: TimeSeriesLocation | null,
): {
  combinedGeoJson: FeatureCollection<
    GeometryObject,
    syncGroupsTypes.PotentialDataProperties
  >;
  splitGeoJsonByCollection: GeoJsonWithService[];
} => {
  const initialAccumulator: {
    obj: Record<
      string,
      Feature<
        GeometryObject,
        {
          names: NonNullable<syncGroupsTypes.PotentialDataProperties['names']>;
          occurrences: {
            id: string;
            properties: UseGetLocationReturnTypeProperties;
          }[];
        }
      >
    >;
    collectionMap: Record<string, GeoJsonWithService>;
  } = {
    obj: {},
    collectionMap: {},
  };

  const { obj, collectionMap } = locations.reduce((_acc, location) => {
    if (!location) {
      return _acc;
    }
    const acc = { ..._acc };
    location.features.forEach((feature) => {
      if (!feature.geometry) {
        return;
      }

      // Geometrycollection does not have coordinates
      // Single location should not be a geometry collection, exclude it here because otherwise type checking will fail
      const key: string = JSON.stringify(
        (feature.geometry as Exclude<GeometryObject, GeometryCollection>)
          .coordinates,
      );
      const serviceName = services?.find(
        (serviceObject) => serviceObject.id === feature?.properties?.serviceId,
      )?.name;
      const collectionName = feature.properties?.collectionId;

      const nameObj = {
        serviceId: feature?.properties?.serviceId as string,
        serviceName,
        collectionId: collectionName as string,
        collectionName: collectionName as string,
      };

      if (acc.obj[key]) {
        if (
          acc.obj[key].properties.names[
            (feature.properties?.name || feature.id) as string
          ]
        ) {
          acc.obj[key].properties.names[
            (feature.properties?.name || feature.id) as string
          ].push(nameObj);
        } else {
          acc.obj[key].properties.names[
            (feature.properties?.name || feature.id) as string
          ] = [nameObj];
        }
        acc.obj[key].properties.occurrences.push({
          id: feature.id as string,
          properties: feature.properties,
        });
      } else {
        acc.obj[key] = {
          ...(feature as Feature<GeometryObject>),
          properties: {
            names: {
              [(feature.properties?.name || feature.id) as string]: [nameObj],
            },
            occurrences: [
              { id: feature.id as string, properties: feature.properties },
            ],
          },
        };
      }

      if (serviceName && collectionName) {
        if (!acc.collectionMap[collectionName]) {
          const newGeoJsonWithService: GeoJsonWithService = {
            collectionName,
            serviceName,
            geoJson: {
              type: 'FeatureCollection',
              features: [],
            },
          };

          acc.collectionMap[collectionName] = newGeoJsonWithService;
        }

        acc.collectionMap[collectionName].geoJson.features.push(feature);
      }
    });

    return acc;
  }, initialAccumulator);

  const combinedGeoJson: FeatureCollection<
    GeometryObject,
    syncGroupsTypes.PotentialDataProperties
  > = {
    type: 'FeatureCollection',
    features: Object.keys(obj).map((key) => {
      const feature = obj[key as `${number}:${number}`];
      return {
        ...feature,
        properties: {
          ...feature.properties,
          name: Object.keys(feature.properties.names).sort(
            (featureA, featureB) => featureB.length - featureA.length,
          )[0],
          hidden: hideFeatures,
          highlightSelectedFeature: feature.id === memoizedSelectedFeature?.id,
          mapFeatureClass:
            feature.properties.occurrences[0].properties?.mapFeatureClass,
        },
      };
    }),
  };

  return {
    combinedGeoJson,
    splitGeoJsonByCollection: Object.values(collectionMap),
  };
};

export const getNearbyLocation = (
  parameter: Parameter,
  service: TimeSeriesService,
  selectedLocation: PointerLocation,
  geojsonMap: Map<string, GeoJsonWithService>,
): TimeSeriesLocation | null => {
  const collection = geojsonMap.get(
    `${service.name}_${parameter.collectionId}`,
  );
  if (!collection?.geoJson.features?.length) {
    return null;
  }
  const nearest = findNearestPoint(
    selectedLocation,
    collection.geoJson.features,
  );

  if (nearest) {
    const [nearestLon, nearestLat] = getFeatureCenterPoint(nearest);
    return {
      id: nearest.id as string,
      lon: nearestLon,
      lat: nearestLat,
    };
  }

  return null;
};

export const getPlotsWithParameters = (
  plots: Plot[],
  parameters: ParameterWithData[],
  plotErrors?: PlotErrorsMap,
  failedParameters?: FailedParametersMap,
): PlotWithData[] => {
  const parametersByPlotId = groupBy(
    parameters,
    (parameter) => parameter.plotId,
  );

  const plotsWithParameters = plots
    .map((plot) => {
      return {
        ...plot,
        parametersWithData: parametersByPlotId[plot.plotId] || [],
        errors: plotErrors?.[plot.plotId],
        failedParameters: failedParameters?.[plot.plotId],
      };
    })
    .filter(
      (plot) =>
        // only keep plots that have parameters as eCharts requires at least one parameter in a plot
        plot.parametersWithData?.length,
    );
  return plotsWithParameters;
};

export function consolidateFeatures<P>(
  features: {
    id: string;
    geoJSON: FeatureCollection<GeometryObject, P>;
  }[],
): FeatureCollection<GeometryObject, P> {
  const fuseFeatures = features.flatMap((feature) => feature.geoJSON.features);

  return {
    type: 'FeatureCollection',
    features: fuseFeatures,
  };
}

export const combineParametersWithData = (
  parameters: Parameter[],
  data: ParameterWithData[],
): ParameterWithData[] => {
  return parameters.map((parameter) => {
    // for each parameter, find the corresponding data point
    const dataPoint = data.find(
      (item) =>
        item.propertyName === parameter.propertyName &&
        item.serviceId === parameter.serviceId &&
        item.collectionId === parameter.collectionId &&
        item.instanceId === parameter.instanceId &&
        serializeDimensions(item.dimensions) ===
          serializeDimensions(parameter.dimensions),
    );
    return {
      ...parameter,
      timestep: dataPoint?.timestep || [],
      value: dataPoint?.value || [],
      unit: dataPoint?.unit || parameter.unit || '',
    };
  });
};

export interface ParameterCollectionQueryData {
  serviceId: string;
  service: TimeSeriesService;
  collectionId: string;
}

export const extractUsedCollections = (
  plotsWithoutData: PlotPreset,
  services: TimeSeriesService[],
): Record<string, ParameterCollectionQueryData> => {
  return plotsWithoutData.parameters.reduce<
    Record<string, ParameterCollectionQueryData>
  >((acc, param) => {
    const serviceCollection = `${param.serviceId}-${param.collectionId}`;
    if (acc.serviceCollection) {
      return acc;
    }
    const service = services.find((service) => service.id === param.serviceId);
    if (!service) {
      return acc;
    }
    return {
      ...acc,
      [serviceCollection]: {
        serviceId: param.serviceId,
        service,
        collectionId: param.collectionId,
      },
    };
  }, {});
};

export const extractDataFromfetchEdrParameterResponse = (
  edrResponse:
    | EDRPositionResponse
    | EDRPositionResponseCoverageCollection
    | null,
  parameter: PlotParameter,
): ParameterWithData | null => {
  // If coverage collection is returned, take the first
  const paramData = (edrResponse as EDRPositionResponseCoverageCollection)
    ?.coverages
    ? (edrResponse as EDRPositionResponseCoverageCollection)?.coverages[0]
    : (edrResponse as EDRPositionResponse);
  if (
    !edrResponse ||
    !paramData?.ranges ||
    !paramData.domain.axes.t ||
    paramData.domain.axes.t.values.length === 0
  ) {
    return null;
  }
  // assume only one parameter
  const paramKey = Object.keys(paramData.ranges)[0];
  const unit = getUnit(
    edrResponse.parameters[paramKey],
    i18next.language,
    'en',
  );
  return {
    ...parameter,
    unit,
    timestep: paramData.domain.axes.t.values.map((str: string) =>
      dateUtils.utc(str),
    ),
    value: paramData.ranges[paramKey].values,
  };
};
